// src/pages/ProductPage.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { getVariantPrice, isAvailableIn } from "../utils/pricing";
import Navigation from "../components/Common/header/Navigation";
import Footer from "../components/Common/footer/Footer";
import ColorGuideModal from "../components/ColorGuideModal";
import "../styles/ProductPage.css";

const ProductPage = () => {
    const { id } = useParams();
    const { addToCart } = useCart();
    const { currency, formatPrice, priceFor } = useCurrency();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Dynamic Selectors
    const [selectedType, setSelectedType] = useState("");
    const [selectedColor, setSelectedColor] = useState("");
    const [selectedTexture, setSelectedTexture] = useState("");
    const [selectedLength, setSelectedLength] = useState("");

    // Modal State
    const [isColorGuideOpen, setIsColorGuideOpen] = useState(false);

    const sortNumeric = (arr) => {
        if (!arr) return [];
        return [...arr].sort((a, b) => (parseInt(a) || 0) - (parseInt(b) || 0));
    };

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const snap = await getDoc(doc(db, "products", id));
                if (snap.exists()) {
                    const data = snap.data();

                    // ON-THE-FLY UPGRADE: Converts old database formats to the new Matrix format for the UI
                    let oColors = data.optionColors || [];
                    let oLengths = data.optionLengths || [];
                    let matrix = data.pricingMatrix || {};
                    let cImages = data.colorImages || {};

                    if (!data.pricingMatrix && data.colors && typeof data.colors[0] === 'object') {
                        data.colors.forEach(c => {
                            if (!oColors.includes(c.name)) oColors.push(c.name);
                            if (c.image) cImages[c.name] = c.image;
                            c.lengths?.forEach(l => {
                                if (!oLengths.includes(l.length.toString())) oLengths.push(l.length.toString());
                                matrix[`${c.name}|${l.length}||`] = l.price;
                            });
                        });
                    }

                    const productData = {
                        id: snap.id,
                        name: data.name || "Unnamed Product",
                        price: data.price || 0,
                        images: data.images || [],
                        description: data.description || "",
                        soldOut: data.soldOut || false,
                        category: data.category || "Uncategorized",
                        optionColors: oColors,
                        optionLengths: sortNumeric(oLengths),
                        optionTextures: data.optionTextures || [],
                        optionTypes: data.optionTypes || [],
                        pricingMatrix: matrix,
                        pricingMatrixUSD: data.pricingMatrixUSD || {},
                        colorImages: cImages
                    };

                    setProduct(productData);

                    // Set Defaults automatically to the first available option
                    setSelectedType(productData.optionTypes[0] || "");
                    setSelectedColor(productData.optionColors[0] || "");
                    setSelectedTexture(productData.optionTextures[0] || "");
                    setSelectedLength(productData.optionLengths[0] || "");
                } else {
                    setError("Product not found");
                }
            } catch (err) {
                setError("Failed to load product.");
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [id]);

    if (loading) return <div className="product-page"><Navigation /><div className="product-page-loading"><div className="spinner" /></div><Footer /></div>;
    if (error || !product) return <div className="product-page"><Navigation /><div className="product-page-error">{error}</div><Footer /></div>;

    // MATRIX PRICING LOGIC
    // Products with no options use the key "|||" (same as the admin pricing table).
    const currentKey = `${selectedColor}|${selectedLength}|${selectedTexture}|${selectedType}`;

    // Price in the visitor's currency (null = this combination isn't sold in that currency)
    const unitPrice = priceFor(product, currentKey);
    const gbpExists = getVariantPrice(product, currentKey, "GBP") !== null;
    const isAvailable = unitPrice !== null;
    const productSoldInCurrency = isAvailableIn(product, currency);

    const unavailableText = currency === "USD" && gbpExists
        ? "Not available for US delivery"
        : "Configuration Unavailable";

    const displayImage = product.colorImages?.[selectedColor] || product.images?.[0] || "https://placehold.co/800x1000";

    const variantLabel = [
        selectedType,
        selectedColor && selectedColor !== 'Standard' ? selectedColor : "",
        selectedTexture,
        selectedLength
    ].filter(Boolean).join(" · ");

    const handleAddToCart = () => {
        if (!isAvailable || product.soldOut) return;

        let cartName = product.name;
        if (selectedType) cartName += ` - ${selectedType}`;
        if (selectedColor && selectedColor !== 'Standard') cartName += ` (${selectedColor})`;
        if (selectedTexture) cartName += ` - ${selectedTexture}`;
        if (selectedLength) cartName += ` - ${selectedLength}`;

        addToCart({
            // Unique per product + option, so different lengths are separate cart lines
            id: `${product.id}__${currentKey}`,
            productId: product.id,
            variantKey: currentKey,
            variantLabel,
            name: cartName,
            image: displayImage,
            category: product.category,
            length: selectedLength,
            selectedType, selectedColor, selectedTexture, selectedLength,
            // Both prices are stored so the cart can switch currency instantly
            priceGBP: getVariantPrice(product, currentKey, "GBP"),
            priceUSD: getVariantPrice(product, currentKey, "USD"),
        });
        alert(`${cartName} has been added to your cart!`);
    };

    // Helper to peek at prices for lengths inside the dropdown
    const getPriceForLength = (len) => {
        const key = `${selectedColor}|${len}|${selectedTexture}|${selectedType}`;
        return priceFor(product, key);
    };

    return (
        <div className="product-page">
            <Helmet>
                <title>{product.name} | Bundle Madness</title>
                <meta name="description" content={product.description ? product.description.substring(0, 155) : `Shop luxury ${product.name} at Bundle Madness. Premium virgin and raw hair extensions.`} />
                <meta property="og:title" content={`${product.name} | Bundle Madness`} />
                <meta property="og:image" content={displayImage} />
            </Helmet>
            <Navigation />
            <div className="product-layout">
                <div className="product-image-panel">
                    <span className="product-category-chip">{product.category}</span>
                    <img src={displayImage} alt={product.name} className="product-main-image" />
                </div>

                <div className="product-info-panel">
                    <p className="product-eyebrow">Bundle Madness</p>
                    <h1 className="product-name">{product.name}</h1>
                    <div className="product-rule" />
                    <p className="product-description">{product.description}</p>

                    {/* ── NEW: Dedicated Color Guide Button (Always Visible) ── */}
                    <div style={{ marginTop: '20px', marginBottom: '25px' }}>
                        <button
                            type="button"
                            onClick={() => setIsColorGuideOpen(true)}
                            style={{
                                background: 'rgba(212, 175, 55, 0.05)',
                                border: '1px solid rgba(212, 175, 55, 0.4)',
                                color: '#d4af37',
                                padding: '10px 18px',
                                fontSize: '0.75rem',
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(212, 175, 55, 0.15)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(212, 175, 55, 0.05)'}
                        >
                            <i className="fas fa-palette"></i> View Color Guide
                        </button>
                    </div>

                    {/* DYNAMIC DROPDOWNS */}
                    {product.optionTypes.length > 0 && (
                        <div className="variant-group" style={{ marginTop: '15px' }}>
                            <span className="variant-label">Select Type</span>
                            <select className="variant-select" value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                                {product.optionTypes.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                    )}

                    {/* The Color Dropdown (Cleaned up!) */}
                    {product.optionColors.length > 0 && (
                        <div className="variant-group" style={{ marginTop: '15px' }}>
                            <span className="variant-label">Select Color</span>
                            <select className="variant-select" value={selectedColor} onChange={(e) => setSelectedColor(e.target.value)}>
                                {product.optionColors.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    )}

                    {product.optionTextures.length > 0 && (
                        <div className="variant-group" style={{ marginTop: '15px' }}>
                            <span className="variant-label">Select Texture</span>
                            <select className="variant-select" value={selectedTexture} onChange={(e) => setSelectedTexture(e.target.value)}>
                                {product.optionTextures.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                    )}

                    {product.optionLengths.length > 0 && (
                        <div className="variant-group" style={{ marginTop: '15px' }}>
                            <span className="variant-label">Select Length</span>
                            <select className="variant-select" value={selectedLength} onChange={(e) => setSelectedLength(e.target.value)}>
                                {product.optionLengths.map(l => {
                                    const peekPrice = getPriceForLength(l);
                                    const priceText = peekPrice !== null ? ` — ${formatPrice(peekPrice)}` : ' (Unavailable)';
                                    return <option key={l} value={l}>{l}{priceText}</option>
                                })}
                            </select>
                        </div>
                    )}

                    <div className="product-price-block">
                        {isAvailable ? (
                            <span className="product-price">{formatPrice(unitPrice)}</span>
                        ) : (
                            <span className="product-price" style={{ color: '#e57373', fontSize: '1.2rem' }}>{unavailableText}</span>
                        )}
                    </div>

                    {currency === "USD" && !productSoldInCurrency && (
                        <p style={{ color: '#a0aec0', fontSize: '0.8rem', marginTop: '-5px' }}>
                            This item is currently only available for UK delivery.
                        </p>
                    )}

                    <button
                        className="product-add-btn"
                        onClick={handleAddToCart}
                        disabled={!isAvailable || product.soldOut}
                        style={{ opacity: (!isAvailable || product.soldOut) ? 0.5 : 1 }}
                    >
                        <span>{product.soldOut ? "Sold Out" : (!isAvailable ? "Not Available" : "Add to Cart")}</span>
                    </button>
                </div>
            </div>

            {/* The Modal Component */}
            <ColorGuideModal
                isOpen={isColorGuideOpen}
                onClose={() => setIsColorGuideOpen(false)}
            />

            <Footer />
        </div>
    );
};

export default ProductPage;